// # Prediction Stats Component for Step 1
import React, { useEffect, useState } from 'react';
import { FaChartLine, FaCalculator } from 'react-icons/fa';
import PropTypes from 'prop-types';
import './stats1_predict.css';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function PredictionStats({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const [nbmeData, setNbmeData] = useState(null);
  const { user } = useAuthStore();

  // # NBME Score prediction formulas
  const nbmeFormulas = {
    'NBME 25': (wrong) => 277.04 - 1.113 * wrong,
    'NBME 26': (wrong) => 276.38 - 1.1275 * wrong,
    'NBME 27': (wrong) => 276.53 - 1.1211 * wrong,
    'NBME 28': (wrong) => 278.36 - 1.1701 * wrong,
    'NBME 29': (wrong) => 276.92 - 1.1522 * wrong,
    'NBME 30': (wrong) => 277.21 - 1.1435 * wrong
  };

  // # UWSA Score prediction formula
  const uwsaFormula = (percentage) => 1.8458 * percentage + 107.6;

  useEffect(() => {
    const fetchNBMEData = async () => {
      // # Get the correct user ID based on roles
      const userId = targetUserId || user?.uid;
      if (!userId) return;

      try {
        const docRef = doc(db, 's1_mentee', userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setNbmeData(docSnap.data()?.nbme || {});
        }
      } catch (error) {
        console.error('Error fetching NBME data:', error);
      }
    };

    fetchNBMEData();
  }, [user, targetUserId]);

  const calculatePredictions = () => {
    if (!nbmeData) return { nbme: [], uwsa: [] };

    const nbmePredictions = Object.entries(nbmeFormulas).map(([exam, formula]) => {
      const examData = nbmeData[exam];
      if (!examData || examData.status !== 'Completed') return null;

      const predictedScore = formula(examData.incorrect);
      return {
        exam,
        incorrect: examData.incorrect,
        predictedScore: Math.round(predictedScore)
      };
    }).filter(Boolean);

    const uwsaPredictions = ['UWSA 1'].map(exam => {
      const examData = nbmeData[exam];
      if (!examData || examData.status !== 'Completed') return null;

      const predictedScore = uwsaFormula(examData.percentage);
      return {
        exam,
        percentage: examData.percentage,
        predictedScore: Math.round(predictedScore)
      };
    }).filter(Boolean);

    return { nbme: nbmePredictions, uwsa: uwsaPredictions };
  };

  const predictions = calculatePredictions();

  return (
    <div className={`stats1_predict_container ${isDarkMode ? 'dark' : ''}`}>
      <div className="stats1_predict_content">
        <div className="stats1_predict_section">
          <div className="stats1_predict_header">
            <FaCalculator className="stats1_predict_icon" />
            <h2>NBME Score Predictions</h2>
          </div>
          <div className="stats1_predict_chart">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={predictions.nbme}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="exam" />
                <YAxis domain={[180, 280]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="predictedScore" 
                  stroke="#9b59b6" 
                  name="Predicted Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="stats1_predict_cards">
            {predictions.nbme.map(pred => (
              <div key={pred.exam} className="stats1_predict_card">
                <h3>{pred.exam}</h3>
                <p className="stats1_predict_score">
                  Predicted: {pred.predictedScore}
                </p>
                <p className="stats1_predict_detail">
                  Based on {pred.incorrect} incorrect answers
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="stats1_predict_section">
          <div className="stats1_predict_header">
            <FaChartLine className="stats1_predict_icon" />
            <h2>UWSA Score Predictions</h2>
          </div>
          <div className="stats1_predict_cards">
            {predictions.uwsa.map(pred => (
              <div key={pred.exam} className="stats1_predict_card uwsa">
                <h3>{pred.exam}</h3>
                <p className="stats1_predict_score">
                  Predicted: {pred.predictedScore}
                </p>
                <p className="stats1_predict_detail">
                  Based on {pred.percentage}% correct
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

PredictionStats.propTypes = {
  isDarkMode: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isTutor: PropTypes.bool,
  isMentorView: PropTypes.bool,
  targetUserId: PropTypes.string
};

export default PredictionStats; 